import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

/**
 * Context-aware ambient themes.
 *
 * MODES:
 *   - dark      → standard dark UI (default fallback)
 *   - light     → bright/airy UI
 *   - dim       → ultra-dark, lowered glows; ambient TV/kiosk
 *   - bedtime   → red-tinted, ultra-dim; phone at night
 *   - kitchen   → high-contrast light; cooking hours on tablet
 *
 * AUTO RESOLUTION (when mode === 'auto'):
 *   1. /display kiosk pages always use 'dim'
 *   2. Phone (mobile) at night (22:00–06:00) → 'bedtime'
 *   3. Tablet during cooking hours (8–10, 12–14, 19–21) → 'kitchen'
 *   4. Daytime (sunrise → sunset) → 'light'
 *   5. Otherwise → 'dark'
 *
 * Sunrise/sunset use the user's stored home_latitude/home_longitude when
 * available; falls back to fixed times otherwise.
 */
const ThemeContext = createContext(null);

const STORAGE_KEY = 'hc_theme_v2';

// Approximate sunrise/sunset for a given date + lat/lng using NOAA's solar calc.
// Returns { sunrise: minutesFromMidnight, sunset: minutesFromMidnight }.
function computeSolarTimes(date, lat = 19.07, lng = 72.87) {
  const rad = Math.PI / 180;
  const day = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 86400000);
  const declination = 23.45 * Math.sin(rad * (360 / 365) * (284 + day));
  const phi = lat * rad;
  const delta = declination * rad;
  const cosOmega = -Math.tan(phi) * Math.tan(delta);
  if (cosOmega < -1 || cosOmega > 1) return { sunrise: 360, sunset: 1080 }; // fallback 6am–6pm
  const omega = Math.acos(cosOmega) / rad; // hour angle in degrees
  const solarNoonUTC = 12 - lng / 15;       // hours UTC when sun is at south
  const tzOffset = -date.getTimezoneOffset() / 60;
  const solarNoonLocal = solarNoonUTC + tzOffset;
  const sunriseHrs = solarNoonLocal - omega / 15;
  const sunsetHrs = solarNoonLocal + omega / 15;
  return {
    sunrise: Math.max(0, Math.min(1440, Math.round(sunriseHrs * 60))),
    sunset: Math.max(0, Math.min(1440, Math.round(sunsetHrs * 60))),
  };
}

function isInWindow(nowMin, start, end) {
  return nowMin >= start && nowMin < end;
}

function isPath(re) {
  return re.test(window.location.pathname);
}

/**
 * Resolves auto mode to a concrete mode based on device + time + location.
 */
export function resolveAutoMode({ deviceType, lat, lng, now = new Date() }) {
  // 0. Kiosk/TV displays: always dim ambient
  if (isPath(/^\/display(\b|\/)/)) return { mode: 'dim', reason: 'kiosk-display' };

  const minutes = now.getHours() * 60 + now.getMinutes();
  const { sunrise, sunset } = computeSolarTimes(now, lat || 19.07, lng || 72.87);

  // 1. Phone at deep night → bedtime (gentler red-tint, ultra-dim)
  if (deviceType === 'mobile' && (minutes >= 22 * 60 || minutes < 6 * 60)) {
    return { mode: 'bedtime', reason: 'phone-night', sunrise, sunset };
  }

  // 2. Tablet during cooking hours → kitchen (high-contrast light)
  const cookingHours = (
    isInWindow(minutes, 8 * 60, 10 * 60) ||      // breakfast
    isInWindow(minutes, 12 * 60, 14 * 60) ||     // lunch
    isInWindow(minutes, 19 * 60, 21 * 60)        // dinner
  );
  if (deviceType === 'tablet' && cookingHours) {
    return { mode: 'kitchen', reason: 'tablet-cooking', sunrise, sunset };
  }

  // 3. Daytime → light
  if (minutes >= sunrise && minutes < sunset) {
    return { mode: 'light', reason: 'daytime', sunrise, sunset };
  }

  // 4. Default → dark
  return { mode: 'dark', reason: 'evening', sunrise, sunset };
}

const VALID_MODES = ['dark', 'light', 'dim', 'bedtime', 'kitchen'];

function getDeviceType() {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

export const ThemeProvider = ({ children }) => {
  // Stored preference: 'auto' or one of VALID_MODES
  const [preference, setPreference] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || 'auto';
  });
  const [homeCoords, setHomeCoords] = useState(() => {
    const lat = parseFloat(localStorage.getItem('hc_home_lat') || '0');
    const lng = parseFloat(localStorage.getItem('hc_home_lng') || '0');
    return { lat: lat || null, lng: lng || null };
  });
  const [tick, setTick] = useState(0);

  // Recompute every 5 minutes so auto-mode keeps up with time changes
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Recompute when window resizes (device type may flip)
  useEffect(() => {
    const onResize = () => setTick(x => x + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Recompute when path changes (kiosk display detection)
  useEffect(() => {
    const onPop = () => setTick(x => x + 1);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Resolve effective mode
  const { effectiveMode, reason, sunrise, sunset } = useMemo(() => {
    if (VALID_MODES.includes(preference)) {
      return { effectiveMode: preference, reason: 'manual', sunrise: null, sunset: null };
    }
    const auto = resolveAutoMode({
      deviceType: getDeviceType(),
      lat: homeCoords.lat,
      lng: homeCoords.lng,
    });
    return {
      effectiveMode: auto.mode,
      reason: auto.reason,
      sunrise: auto.sunrise,
      sunset: auto.sunset,
    };
  }, [preference, homeCoords.lat, homeCoords.lng, tick]); // eslint-disable-line

  // Apply to <html>
  useEffect(() => {
    const root = document.documentElement;
    // Tailwind darkMode='class' → keep .dark on for everything except light/kitchen
    const useDark = !['light', 'kitchen'].includes(effectiveMode);
    root.classList.toggle('dark', useDark);
    root.classList.toggle('light', !useDark);

    // Specific theme class for granular CSS overrides
    VALID_MODES.forEach(m => root.classList.toggle(`theme-${m}`, m === effectiveMode));
    root.dataset.themeMode = effectiveMode;
    root.dataset.themeReason = reason;
  }, [effectiveMode, reason]);

  const setMode = useCallback((newMode) => {
    if (newMode === 'auto' || VALID_MODES.includes(newMode)) {
      localStorage.setItem(STORAGE_KEY, newMode);
      setPreference(newMode);
    }
  }, []);

  const setHomeLocation = useCallback((lat, lng) => {
    if (lat) localStorage.setItem('hc_home_lat', String(lat));
    if (lng) localStorage.setItem('hc_home_lng', String(lng));
    setHomeCoords({ lat: lat || null, lng: lng || null });
  }, []);

  return (
    <ThemeContext.Provider value={{
      // Actually-applied mode (dark / light / dim / bedtime / kitchen)
      mode: effectiveMode,
      // Stored user choice ('auto' or one of VALID_MODES)
      preference,
      setMode,
      // Why auto picked this mode
      reason,
      sunrise,
      sunset,
      isAuto: preference === 'auto',
      validModes: VALID_MODES,
      setHomeLocation,
      // Backward-compat with legacy callers expecting `theme` + `toggleTheme`
      theme: ['light', 'kitchen'].includes(effectiveMode) ? 'light' : 'dark',
      toggleTheme: () => setMode(['light', 'kitchen'].includes(effectiveMode) ? 'dark' : 'light'),
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
